import { Chip, Divider, Grid, MenuItem, Typography } from '@mui/material'
import { Select, TextField } from 'mui-rff'
import { memo, useEffect, useState } from 'react'
import { Form, FormSpy } from 'react-final-form'
import { OnChange } from 'react-final-form-listeners'
import { WbAutocomplete } from 'src/components/WbAutocomplete'
import { MasterWorkout } from 'src/types/MasterWorkout'
import { IntensityTechniques, MasterWorkoutExercise, MasterWorkoutExerciseLookups } from 'src/types/MasterWorkoutExercise'

interface CreateOrEditMasterWorkoutExerciseFormProps {
    masterWorkout: MasterWorkout
    lookups: MasterWorkoutExerciseLookups
    initialValues: MasterWorkoutExercise
    onSubmit: (value: MasterWorkoutExercise) => void
    onValidate: (value: MasterWorkoutExercise) => any
    masterWorkoutWeightSelection: string
    canEdit: boolean
}

const CreateOrEditMasterWorkoutExerciseForm: React.FC<CreateOrEditMasterWorkoutExerciseFormProps> = ({
    masterWorkout,
    initialValues,
    lookups,
    onSubmit,
    onValidate,
    masterWorkoutWeightSelection,
    canEdit,
}) => {
    const { masterExercises, masterGroups, groupAccessTerms, groupFilterTerms } = lookups

    const [showPercentageSettings, setShowPercentageSettings] = useState(
        masterWorkoutWeightSelection === 'percentage' && [8, 6, 3, 0].includes(initialValues.masterGroupId)
    )

    const formFields = (form: any) => {
        return {
            col1: [
                {
                    size: 12,
                    field: (
                        <FormSpy subscription={{ values: true }}>
                            {({ values }) => {
                                form.change('masterGroupName', masterGroups.find((x) => x.value == values.masterGroupId || x.value == 0)?.name)
                                const items = masterWorkout.enableMains ? [{ value: 0, name: 'Main Lift' }, ...masterGroups] : masterGroups
                                return (
                                    <WbAutocomplete
                                        name="masterGroupId"
                                        label="Master Group"
                                        disabled={!canEdit}
                                        required
                                        options={items}
                                        getOptionValue={(x) => {
                                            return x.value
                                        }}
                                        getOptionLabel={(x: any) => {
                                            return x.name
                                        }}
                                        renderOption={(props, option) => {
                                            return (
                                                <li {...props} key={option.value}>
                                                    {option.name}
                                                </li>
                                            )
                                        }}
                                    />
                                )
                            }}
                        </FormSpy>
                    ),
                },
                {
                    size: 12,
                    field: (
                        <>
                            <FormSpy subscription={{ values: true }}>
                                {({ values }) =>
                                    [8, 6, 3, 0].includes(values.masterGroupId) ? null : (
                                        <WbAutocomplete
                                            name="masterWorkoutExerciseGroupAccessTermIds"
                                            label="Choose exercise category"
                                            disabled={!canEdit || [8, 3, 6, 0, null].includes(values.masterGroupId)}
                                            multiple
                                            options={groupAccessTerms}
                                            getOptionValue={(x) => {
                                                return x.value
                                            }}
                                            getOptionLabel={(x: any) => {
                                                return x.name
                                            }}
                                            renderOption={(props, option) => {
                                                return (
                                                    <li {...props} key={option.value}>
                                                        {option.name}
                                                    </li>
                                                )
                                            }}
                                            renderTags={(values, getTagProps) =>
                                                values.map((value, index: number) => {
                                                    return <Chip key="index" variant="outlined" label={value?.name} {...getTagProps({ index })} />
                                                })
                                            }
                                        />
                                    )
                                }
                            </FormSpy>
                        </>
                    ),
                },
                {
                    size: 12,
                    field: (
                        <FormSpy subscription={{ values: true }}>
                            {({ values }) =>
                                [8, 6, 3, 0].includes(values.masterGroupId) ? null : (
                                    <WbAutocomplete
                                        name="masterWorkoutExerciseGroupFilterTermIds"
                                        label="Choose exercise filter"
                                        disabled={!canEdit || [8, 3, 6, 0, null].includes(values.masterGroupId)}
                                        multiple
                                        options={groupFilterTerms}
                                        getOptionValue={(x) => {
                                            return x.value
                                        }}
                                        getOptionLabel={(x: any) => {
                                            return x.name
                                        }}
                                        renderOption={(props, option) => {
                                            return (
                                                <li {...props} key={option.value}>
                                                    {option.name}
                                                </li>
                                            )
                                        }}
                                        renderTags={(values, getTagProps) =>
                                            values.map((value, index: number) => {
                                                return <Chip key="index" variant="outlined" label={value?.name} {...getTagProps({ index })} />
                                            })
                                        }
                                    />
                                )
                            }
                        </FormSpy>
                    ),
                },
                {
                    size: 12,
                    field: (
                        <FormSpy subscription={{ values: true }}>
                            {({ values }) => {
                                let items =
                                    [8, 3, 6, 0, null].includes(values.masterGroupId) && masterWorkout.enableMains // Filter by main lifts
                                        ? masterExercises.filter((x) => masterWorkout.masterWorkoutMaxes.some((max) => max.masterExerciseId == x.value))
                                        : values?.masterWorkoutExerciseGroupAccessTermIds?.length > 0 // Filter by Muscle groups
                                            ? masterExercises.filter((x) => x.masterExerciseGroupAccessTermIds.some((r) => values?.masterWorkoutExerciseGroupAccessTermIds?.includes(r)))
                                            : masterExercises

                                if (values.masterWorkoutExerciseGroupFilterTermIds?.length > 0) {
                                    items = items.filter(x =>
                                        x.masterExerciseGroupFilterTermIds.some(termId =>
                                            values.masterWorkoutExerciseGroupFilterTermIds.includes(termId)
                                        )
                                    );

                                    if (!items.some(x => x.value === values.masterExerciseId)) {
                                        form.change('masterExerciseId', null);
                                    }
                                }

                                form.change('masterExerciseName', items.find((x) => x.value == values.masterExerciseId)?.name || values.masterExerciseName || '')

                                return (
                                    <WbAutocomplete
                                        name="masterExerciseId"
                                        label="Choose suggested exercise"
                                        disabled={!canEdit}
                                        options={items}
                                        required
                                        getOptionValue={(x) => {
                                            return x.value
                                        }}
                                        getOptionLabel={(x: any) => {
                                            return x.name
                                        }}
                                        renderOption={(props, option) => {
                                            return (
                                                <li {...props} key={option.value}>
                                                    {option.name}
                                                </li>
                                            )
                                        }}
                                    />
                                )
                            }}
                        </FormSpy>
                    ),
                },
                {
                    size: 8,
                    field: <TextField type="number" label="Reps" name="reps" />,
                },
                {
                    size: 8,
                    field: <TextField type="text" label="RepRange" name="repRange" />,
                },
                {
                    size: 8,
                    field: <TextField type="text" label="Rir" name="rir" />,
                },
                {
                    size: 8,
                    field: <TextField type="text" label="Rest" name="rest" helperText="Example: 1-2 min" />,
                },
                // TODO: RIR / RPE ?
                {
                    size: 8,
                    field: <TextField type="text" label="Tempo" name="tempo" helperText="Example: 1221" />,
                },
                {
                    size: 8,
                    field: (
                        <Select name="intensityTechnique" label="Intensity Technique" disabled={!canEdit}>
                            <MenuItem key={null} value={null}>
                                None
                            </MenuItem>
                            {Object.entries(IntensityTechniques).map(([value, name]) => (
                                <MenuItem key={value} value={value}>
                                    {name}
                                </MenuItem>
                            ))}
                        </Select>
                    ),
                },
                {
                    size: 8,
                    field: <TextField type="text" label="Note" name="note" helperText="Display below the instructions" />,
                },
            ],
            col2: [
                {
                    size: 8,
                    field: <TextField type="number" label="Percentage of max" name="percentageOfMax" helperText="Example: 0.75" />,
                },
                {
                    size: 8,
                    field: <TextField type="number" label="Max Conservative" name="maxConservative" helperText="Example: 0.75" />,
                },
                {
                    size: 8,
                    field: <TextField type="number" label="Max Moderate" name="maxModerate" helperText="Example: 0.75" />,
                },
                {
                    size: 8,
                    field: <TextField type="number" label="Max High" name="maxHigh" helperText="Example: 0.75" />,
                },
                {
                    size: 8,
                    field: <TextField type="number" label="Multiplier" name="multiplier" helperText="Example: 0.75" />,
                },
            ],
        }
    }

    return (
        <Form
            onSubmit={onSubmit}
            initialValues={initialValues}
            validate={onValidate}
            subscription={{ initialValues: true }}
            render={({ handleSubmit, form }) => {
                const handleAutoSubmit = (values: MasterWorkoutExercise) => {
                    onSubmit(values)
                    form.reset(values)
                }

                return (
                    <form onSubmit={handleSubmit} noValidate>
                        <fieldset disabled={!canEdit} style={{ border: 'none' }}>
                            <Grid container alignItems="flex-start" spacing={2}>
                                {formFields(form).col1.map((item, idx) => (
                                    <Grid item xs={item.size} key={idx}>
                                        {item.field}
                                    </Grid>
                                ))}
                                <Grid item xs={12}>
                                    <Divider variant="fullWidth" />
                                </Grid>

                                {showPercentageSettings && (
                                    <>
                                        <Grid item xs={12}>
                                            <Typography variant="h4">Percentage Program Settings</Typography>
                                        </Grid>

                                        {formFields(form).col2.map((item, idx) => (
                                            <Grid item xs={item.size} key={idx}>
                                                {item.field}
                                            </Grid>
                                        ))}
                                    </>
                                )}
                            </Grid>
                        </fieldset>

                        <OnChange name="masterGroupId">
                            {(value, previous) => {
                                if ([8, 6, 3, 0, null].includes(value)) form.change('masterWorkoutExerciseGroupAccessTermIds', [])
                                setShowPercentageSettings(masterWorkoutWeightSelection == 'percentage' && [8, 6, 3, 0].includes(value))
                            }}
                        </OnChange>

                        <FormSpy subscription={{ values: true, pristine: true }}>
                            {({ values, pristine }) => {
                                if (!pristine && values.week == initialValues.week && values.day == initialValues.day && values.displayOrder == initialValues.displayOrder) {
                                    handleAutoSubmit(values as MasterWorkoutExercise)
                                }
                                return null
                            }}
                        </FormSpy>
                    </form>
                )
            }}
        />
    )
}

export default memo(CreateOrEditMasterWorkoutExerciseForm)
